import { lazy, Suspense } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth, isPowerUser } from './contexts/AuthContext';
import { NavigationProvider } from './contexts/NavigationContext';
import { CreditsProvider } from './contexts/CreditsContext';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { LoadingScreen } from './components/common/LoadingScreen';
import { getProfile } from './services/profileService';
import { supabase } from './lib/supabase';
import { getTrustHost } from './lib/trust';
import { useUiPreference, type UiPreference } from './hooks/useUiPreference';

// ============================================================================
// LAZY-LOADED PAGES — each becomes a separate chunk for faster initial load
// ============================================================================

// Auth (loaded eagerly since it's the entry point)
import { LoginForm } from './components/auth/LoginForm';

// Legal pages (public, lazy)
const PrivacyPolicy = lazy(() => import('./pages/legal').then(m => ({ default: m.PrivacyPolicy })));
const TermsOfService = lazy(() => import('./pages/legal').then(m => ({ default: m.TermsOfService })));
const DataProcessingAgreement = lazy(() => import('./pages/legal').then(m => ({ default: m.DataProcessingAgreement })));
const SubProcessors = lazy(() => import('./pages/legal').then(m => ({ default: m.SubProcessors })));
const CookiePolicy = lazy(() => import('./pages/legal').then(m => ({ default: m.CookiePolicy })));

// Core pages (lazy)
const ProfileOnboarding = lazy(() => import('./pages/ProfileOnboarding').then(m => ({ default: m.ProfileOnboarding })));
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const Inbox = lazy(() => import('./pages/Inbox').then(m => ({ default: m.Inbox })));
const ProfileDashboard = lazy(() => import('./pages/ProfileDashboard').then(m => ({ default: m.ProfileDashboard })));
const ActionTracker = lazy(() => import('./pages/ActionTracker').then(m => ({ default: m.ActionTracker })));
const AIAdviser = lazy(() => import('./pages/AIAdviser').then(m => ({ default: m.AIAdviser })));
const DataRegisterEnhanced = lazy(() => import('./pages/DataRegisterEnhanced').then(m => ({ default: m.DataRegisterEnhanced })));
const ConsentManagement = lazy(() => import('./pages/ConsentManagement').then(m => ({ default: m.ConsentManagement })));
const ConsentForms = lazy(() => import('./pages/ConsentForms').then(m => ({ default: m.ConsentForms })));
const UpgradeRequired = lazy(() => import('./pages/UpgradeRequired').then(m => ({ default: m.UpgradeRequired })));
const AccessRequests = lazy(() => import('./pages/AccessRequests').then(m => ({ default: m.AccessRequests })));
const BreachLogging = lazy(() => import('./pages/BreachLogging').then(m => ({ default: m.BreachLogging })));
const CreditsManagement = lazy(() => import('./pages/CreditsManagement').then(m => ({ default: m.CreditsManagement })));
const ComplianceHub = lazy(() => import('./pages/ComplianceHub').then(m => ({ default: m.ComplianceHub })));
const ComplianceReportGenerator = lazy(() => import('./pages/ComplianceReportGenerator'));
const DPMP = lazy(() => import('./pages/DPMP').then(m => ({ default: m.DPMP })));
const DPIAManager = lazy(() => import('./pages/DPIAManager').then(m => ({ default: m.DPIAManager })));
const PolicyManager = lazy(() => import('./pages/PolicyManager').then(m => ({ default: m.PolicyManager })));
const ManualOfAuthorities = lazy(() => import('./pages/ManualOfAuthorities').then(m => ({ default: m.ManualOfAuthorities })));
const Documents = lazy(() => import('./pages/Documents').then(m => ({ default: m.Documents })));
const EvidenceLibrary = lazy(() => import('./pages/EvidenceLibrary').then(m => ({ default: m.EvidenceLibrary })));
const AuditLog = lazy(() => import('./pages/AuditLog').then(m => ({ default: m.AuditLog })));
const DPORegistry = lazy(() => import('./pages/DPORegistry').then(m => ({ default: m.DPORegistry })));
const RetentionSchedule = lazy(() => import('./pages/RetentionSchedule').then(m => ({ default: m.RetentionSchedule })));
const DocumentDestruction = lazy(() => import('./pages/DocumentDestruction').then(m => ({ default: m.DocumentDestruction })));
const Training = lazy(() => import('./pages/Training').then(m => ({ default: m.Training })));
const CrossBorderTransfers = lazy(() => import('./pages/CrossBorderTransfers').then(m => ({ default: m.CrossBorderTransfers })));
const NotificationsCenter = lazy(() => import('./pages/NotificationsCenter').then(m => ({ default: m.NotificationsCenter })));
const UserManagement = lazy(() => import('./pages/UserManagement').then(m => ({ default: m.UserManagement })));
const SetPassword = lazy(() => import('./pages/SetPassword').then(m => ({ default: m.SetPassword })));
const Help = lazy(() => import('./pages/Help').then(m => ({ default: m.Help })));
const DSARPortal = lazy(() => import('./pages/DSARPortal').then(m => ({ default: m.DSARPortal })));
const VendorManagement = lazy(() => import('./pages/VendorManagement').then(m => ({ default: m.VendorManagement })));
const PrivacyCentre = lazy(() => import('./pages/public/PrivacyCentre').then(m => ({ default: m.PrivacyCentre })));
const PublicDirectory = lazy(() => import('./pages/public/PublicDirectory').then(m => ({ default: m.PublicDirectory })));
const PublicTrustPage = lazy(() => import('./pages/PublicTrustPage').then(m => ({ default: m.PublicTrustPage })));
const ReportConcern = lazy(() => import('./pages/ReportConcern').then(m => ({ default: m.ReportConcern })));
const PublicPolicyPage = lazy(() => import('./pages/public/PublicPolicyPage').then(m => ({ default: m.PublicPolicyPage })));
const ConsentCapture = lazy(() => import('./pages/public/ConsentCapture').then(m => ({ default: m.ConsentCapture })));
const ConsentWithdraw = lazy(() => import('./pages/public/ConsentWithdraw').then(m => ({ default: m.ConsentWithdraw })));

// Layout (loaded eagerly — used on every authenticated page)
import { DashboardLayout } from './components/layout/DashboardLayout';
import { DashboardLayoutNew } from './components/layout/DashboardLayoutNew';
import { CookieConsentBanner } from './components/common/CookieConsentBanner';

// BugBot widget type declaration
declare global {
  interface Window {
    BugBot?: {
      init: (options: Record<string, unknown>) => void;
      destroy: () => void;
      _initialized: boolean;
    };
  }
}

// ============================================================================
// AUTH SCREEN (LOGIN)
// ============================================================================

function AuthScreen() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, loading, navigate]);

  if (loading) {
    return <LoadingScreen message="Loading PDPO Assist..." />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-100 flex items-center justify-center p-4">
      <LoginForm />
    </div>
  );
}

function SurfaceUnknown() {
  const { signOut } = useAuth();
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold text-slate-900">
          We could not check your account
        </h1>
        <p className="mt-2 text-slate-600">
          Reload the page. If it keeps happening, email support@pdpoassist.com and
          we will sort it out.
        </p>
        <div className="mt-5 flex items-center justify-center gap-3">
          <button
            onClick={() => window.location.reload()}
            className="rounded-lg bg-blue-600 px-4 py-2 font-medium text-white hover:bg-blue-700"
          >
            Reload
          </button>
          <button
            onClick={() => signOut()}
            className="text-sm text-slate-500 hover:text-slate-800"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// ROLE-BASED HOME: Decides where users land after login
// ============================================================================

function RoleBasedHome() {
  const { userProfile, loading } = useAuth();
  const [uiPref] = useUiPreference();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!userProfile) {
    return <Navigate to="/login" replace />;
  }

  const pdpoRole = userProfile.primaryPdpoRole;

  // Staff or no PDPO role → My Actions (light version)
  if (!pdpoRole || pdpoRole.roleCode === 'STAFF' || pdpoRole.hierarchyLevel >= 4) {
    return <Navigate to="/actions" replace />;
  }

  // Power users (DPO, CEO, CFO, LEGAL, DEPT_HEAD, MANAGER, SUPERVISOR) →
  // classic Dashboard or new Inbox depending on stored preference.
  if (isPowerUser(userProfile)) {
    return <Navigate to={uiPref === 'new' ? '/inbox' : '/dashboard'} replace />;
  }

  // Fallback to actions
  return <Navigate to="/actions" replace />;
}


// ============================================================================
// ROUTE GUARDS
// ============================================================================

interface RequirePowerUserProps {
  children: React.ReactNode;
}

function RequirePowerUser({ children }: RequirePowerUserProps) {
  const { userProfile, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isPowerUser(userProfile)) {
    return <Navigate to="/actions" replace />;
  }

  return <>{children}</>;
}

interface RequirePdpoRoleProps {
  allowed: string[];
  children: React.ReactNode;
}

function RequirePdpoRole({ allowed, children }: RequirePdpoRoleProps) {
  const { userProfile, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  const roleCode = userProfile?.primaryPdpoRole?.roleCode;

  if (!roleCode || !allowed.includes(roleCode)) {
    return <Navigate to="/actions" replace />;
  }

  return <>{children}</>;
}

// ============================================================================
// SUSPENSE FALLBACK for lazy-loaded routes
// ============================================================================

function PageLoader() {
  return (
    <div className="flex items-center justify-center p-12">
      <div className="text-center">
        <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mx-auto mb-3 animate-pulse">
          <span className="text-white font-bold text-sm">PA</span>
        </div>
        <p className="text-sm text-slate-500">Loading page...</p>
      </div>
    </div>
  );
}

// ============================================================================
// PROTECTED ROUTES
// ============================================================================

function ProtectedRoutes() {
  const { user, userProfile, loading, fetchUserProfile, surface } = useAuth();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [checkingProfile, setCheckingProfile] = useState(true);
  const [uiPref, setUiPref] = useUiPreference();
  const location = useLocation();
  const navigate = useNavigate();

  // ?ui=new / ?ui=classic — write the preference, strip the param.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ui = params.get('ui');
    if (ui !== 'new' && ui !== 'classic') return;
    setUiPref(ui as UiPreference);
    params.delete('ui');
    const nextSearch = params.toString();
    navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`, { replace: true });
  }, [location.pathname, location.search, navigate, setUiPref]);

  useEffect(() => {
    if (loading) return;

    if (!user) {
      setCheckingProfile(false);
      return;
    }

    const run = async () => {
      if (!user) {
        setShowOnboarding(false);
        setCheckingProfile(false);
        return;
      }

      try {
        let orgId = userProfile?.organisation_id;

        if (!orgId) {
          const { data: dbUser } = await supabase
            .from('users')
            .select('organisation_id')
            .eq('auth_user_id', user.id)
            .maybeSingle();

          orgId = dbUser?.organisation_id;

          if (!orgId) {
            const fetched = await fetchUserProfile(user.id);
            orgId = fetched?.organisation_id ?? dbUser?.organisation_id;
          }

          if (!orgId) {
            setShowOnboarding(true);
            setCheckingProfile(false);
            return;
          }
        }

        const profile = await getProfile(orgId);
        const shouldShow = !profile || !profile.profile_completed_at;
        setShowOnboarding(shouldShow);
      } catch (err) {
        console.error('[Onboarding] Failed to check profile:', err);
        setShowOnboarding(true);
      } finally {
        setCheckingProfile(false);
      }
    };

    run();
  }, [loading, user, userProfile, fetchUserProfile]);

  // BugBot feedback widget — loads once for authenticated users.
  // Not on the upgrade screen: it is the one page in this product that sells,
  // and a floating chat bubble over it is a distraction on the only page where
  // the reader's attention is already worth something.
  useEffect(() => {
    if (!userProfile || surface !== 'main' || window.BugBot?._initialized) return;

    const script = document.createElement('script');
    script.src = 'https://bugbot-api.lexabn.workers.dev/widget/bugbot.js?v=6';
    script.async = true;
    script.onload = () => {
      window.BugBot?.init({
        appId: 'pdpo-assist',
        apiBaseUrl: 'https://bugbot-api.lexabn.workers.dev',
        user: { userId: userProfile.id, name: userProfile.name, email: userProfile.email },
      });
    };
    document.body.appendChild(script);

    return () => {
      window.BugBot?.destroy();
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [userProfile, surface]);

  if (loading) {
    return <LoadingScreen message="Loading PDPO Assist..." />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (checkingProfile) {
    return <LoadingScreen message="Loading PDPO Assist..." />;
  }

  // Checked before onboarding: walking a Go owner through main-app setup they
  // cannot use would be worse than a wall.
  if (surface === undefined) {
    return <LoadingScreen message="Loading PDPO Assist..." />;
  }

  if (surface === 'go') {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <UpgradeRequired />
      </Suspense>
    );
  }

  // Resolved to nothing, or to a value nobody defined. Not permission — but not
  // an upgrade pitch either, because the likeliest cause is a failed lookup by
  // somebody who has already paid for this.
  if (surface !== 'main') {
    return <SurfaceUnknown />;
  }

  if (showOnboarding) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <ProfileOnboarding
          isEditMode={isEditMode}
          onComplete={() => { setShowOnboarding(false); setIsEditMode(false); }}
        />
      </Suspense>
    );
  }

  const Layout = uiPref === 'new' ? DashboardLayoutNew : DashboardLayout;

  return (
    <CreditsProvider>
      <NavigationProvider>
        <Layout>
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* DEFAULT ROUTE */}
                <Route path="/" element={<RoleBasedHome />} />

                {/* POWER USER ROUTES */}
                <Route path="/dashboard" element={<RequirePowerUser><Dashboard /></RequirePowerUser>} />
                <Route path="/inbox" element={<RequirePowerUser><Inbox /></RequirePowerUser>} />
                <Route path="/compliance" element={<RequirePowerUser><ComplianceHub /></RequirePowerUser>} />
                <Route path="/compliance/report/new" element={<RequirePowerUser><ComplianceReportGenerator /></RequirePowerUser>} />
                <Route path="/data-register" element={<RequirePowerUser><DataRegisterEnhanced /></RequirePowerUser>} />
                <Route path="/consent" element={<RequirePowerUser><ConsentManagement /></RequirePowerUser>} />
                <Route path="/consent-forms" element={<RequirePowerUser><ConsentForms /></RequirePowerUser>} />
                <Route path="/access-requests" element={<RequirePowerUser><AccessRequests /></RequirePowerUser>} />
                <Route path="/breaches" element={<RequirePowerUser><BreachLogging /></RequirePowerUser>} />
                <Route path="/policies" element={<RequirePowerUser><PolicyManager /></RequirePowerUser>} />
                <Route path="/dpmp" element={<RequirePowerUser><DPMP /></RequirePowerUser>} />
                <Route path="/dpia" element={<RequirePowerUser><DPIAManager /></RequirePowerUser>} />
                <Route path="/cross-border" element={<RequirePowerUser><CrossBorderTransfers /></RequirePowerUser>} />
                <Route path="/retention" element={<RequirePowerUser><RetentionSchedule /></RequirePowerUser>} />
                <Route path="/documents-held" element={<RequirePowerUser><DocumentDestruction /></RequirePowerUser>} />
                <Route path="/evidence" element={<RequirePowerUser><EvidenceLibrary /></RequirePowerUser>} />
                <Route path="/dpo-registry" element={<RequirePowerUser><DPORegistry /></RequirePowerUser>} />
                <Route path="/vendors" element={<RequirePowerUser><VendorManagement /></RequirePowerUser>} />
                <Route path="/public-page" element={<RequirePowerUser><PublicTrustPage /></RequirePowerUser>} />
                <Route path="/audit-log" element={<RequirePowerUser><AuditLog /></RequirePowerUser>} />
                <Route path="/credits" element={<RequirePowerUser><CreditsManagement /></RequirePowerUser>} />

                {/* HIGHLY RESTRICTED ROUTES */}
                <Route path="/user-management" element={<RequirePdpoRole allowed={['DPO', 'CEO', 'CFO']}><UserManagement /></RequirePdpoRole>} />
                <Route path="/authorities" element={<RequirePdpoRole allowed={['DPO', 'CEO', 'CFO']}><ManualOfAuthorities /></RequirePdpoRole>} />

                {/* ROUTES AVAILABLE TO EVERYONE */}
                <Route path="/actions" element={<ActionTracker />} />
                <Route path="/ai-adviser" element={<AIAdviser />} />
                <Route path="/documents" element={<Documents />} />
                <Route path="/training" element={<Training />} />
                <Route path="/profile" element={<ProfileDashboard onEdit={() => { setIsEditMode(true); setShowOnboarding(true); }} />} />
                <Route path="/notifications" element={<NotificationsCenter />} />
                <Route path="/help" element={<Help />} />

                {/* FALLBACK */}
                <Route path="*" element={<RoleBasedHome />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </Layout>
      </NavigationProvider>
    </CreditsProvider>
  );
}

// ============================================================================
// MAIN APP
// ============================================================================

// ============================================================================
// TRUST-HOST ROUTING
// trust.pdpo.ai            → public landing page + organisation directory
// <slug>.trust.pdpo.ai     → that organisation's hosted privacy centre
// Host detection lives in lib/trust.ts, shared with the public pages.
// ============================================================================

export default function App() {
  const trustHost = getTrustHost();

  // On a trust host the app IS the public layer — no login, no protected
  // routes. The bare host serves the directory landing page; a subdomain
  // serves that organisation's privacy centre. Company pages, the DSAR
  // portal and legal pages stay reachable so navigation never leaves the
  // branded host.
  if (trustHost) {
    return (
      <BrowserRouter>
        <ErrorBoundary>
          <Suspense fallback={<LoadingScreen message="Loading..." />}>
            <Routes>
              <Route path="/dsar/:token" element={<DSARPortal />} />
              <Route path="/c/:token" element={<ConsentCapture />} />
              <Route path="/w/:token" element={<ConsentWithdraw />} />
              <Route path="/report/:token" element={<ReportConcern />} />
              <Route path="/p/:slug" element={<PrivacyCentre />} />
              <Route path="/p/:slug/policy/:policyId" element={<PublicPolicyPage />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route
                path="*"
                element={
                  trustHost.kind === 'centre' ? (
                    <PrivacyCentre slugOverride={trustHost.slug} />
                  ) : (
                    <PublicDirectory />
                  )
                }
              />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <CookieConsentBanner />

      <ErrorBoundary>
        <Suspense fallback={<LoadingScreen message="Loading PDPO Assist..." />}>
          <Routes>
            {/* PUBLIC ROUTES */}
            <Route path="/login" element={<AuthScreen />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/dpa" element={<DataProcessingAgreement />} />
            <Route path="/subprocessors" element={<SubProcessors />} />
            <Route path="/cookies" element={<CookiePolicy />} />
            <Route path="/dsar/:token" element={<DSARPortal />} />
            <Route path="/c/:token" element={<ConsentCapture />} />
            <Route path="/w/:token" element={<ConsentWithdraw />} />
            <Route path="/report/:token" element={<ReportConcern />} />
            <Route path="/p/:slug" element={<PrivacyCentre />} />
            <Route path="/p/:slug/policy/:policyId" element={<PublicPolicyPage />} />
            <Route path="/directory" element={<PublicDirectory />} />

            {/* PROTECTED APP */}
            <Route path="/*" element={<ProtectedRoutes />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
}
